import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, ChevronRight, Users, CalendarDays, Loader2 } from "lucide-react";
import { SiteHeader, SiteFooter } from "@/pages/Landing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_TRIPS = [
  { id: "wreck_day", name: "Wreck Fishing Day", price_gbp: 130, per_angler: true, capacity: 12, blurb: "Giant pollock, bass, John Dory, ling & cod over historic 10-mile wrecks." },
  { id: "tuna_whole", name: "Private Bluefin Tuna Day — Whole Boat", price_gbp: 1350, per_angler: false, capacity: 1, blurb: "Exclusive charter for 4–6 anglers at Start Point." },
  { id: "tuna_seat", name: "Individual Bluefin Tuna Seat", price_gbp: 350, per_angler: true, capacity: 6, blurb: "A single spot on a shared tuna expedition." },
];

const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const isoDay = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

export default function Book() {
  const [params] = useSearchParams();
  const [trips, setTrips] = useState(FALLBACK_TRIPS);
  const [tripId, setTripId] = useState(params.get("trip") || "wreck_day");
  const [cursor, setCursor] = useState(() => new Date());
  const [availability, setAvailability] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const trip = trips.find((t) => t.id === tripId) || FALLBACK_TRIPS[0];
  const todayIso = useMemo(() => {
    const n = new Date();
    return isoDay(n.getFullYear(), n.getMonth(), n.getDate());
  }, []);

  useEffect(() => {
    axios.get(`${API}/trips`).then((r) => setTrips(r.data.trips)).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/availability`, { params: { month: monthKey(cursor) } })
      .then((r) => setAvailability(r.data.availability))
      .catch(() => setAvailability({}));
  }, [cursor]);

  const remaining = (iso) => {
    const m = availability[tripId];
    if (!m || m[iso] === undefined) return null;
    return m[iso];
  };

  const grid = useMemo(() => {
    const y = cursor.getFullYear();
    const m = cursor.getMonth();
    const first = new Date(y, m, 1);
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const lead = (first.getDay() + 6) % 7; // Monday-first
    const cells = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(isoDay(y, m, d));
    return cells;
  }, [cursor]);

  const maxQty = selectedDate ? Math.min(trip.capacity, remaining(selectedDate) ?? trip.capacity) : trip.capacity;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!selectedDate) { setError("Pick a date on the calendar first."); return; }
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) { setError("Name, phone and email are all needed."); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/bookings/checkout`, {
        trip_id: tripId,
        date: selectedDate,
        quantity: trip.per_angler ? quantity : 1,
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      setError(err.response?.data?.detail || "Could not start checkout — please try again or call the skipper.");
      setSubmitting(false);
    }
  };

  const total = trip.price_gbp * (trip.per_angler ? quantity : 1);

  return (
    <div className="min-h-screen bg-stone-900 text-stone-100 antialiased" data-testid="booking-page">
      <SiteHeader />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <span className="text-amber-400 font-mono text-xs uppercase tracking-widest font-bold">Live Availability · Apple Pay & Card</span>
        <h1 className="text-3xl sm:text-5xl font-extrabold uppercase tracking-tight text-white mt-2 mb-10">Book Your Date</h1>

        <form onSubmit={submit} className="grid lg:grid-cols-12 gap-10">
          {/* Trip picker */}
          <div className="lg:col-span-5 space-y-4">
            <h2 className="text-xs font-bold uppercase tracking-widest text-stone-400">1 · Choose Your Trip</h2>
            {trips.map((t) => (
              <button type="button" key={t.id} data-testid={`trip-option-${t.id}`}
                onClick={() => { setTripId(t.id); setQuantity(1); setSelectedDate(null); }}
                className={`w-full text-left rounded-xl p-5 border transition-colors ${tripId === t.id ? "border-amber-400 bg-stone-950 shadow-xl" : "border-stone-800 bg-stone-950/60 hover:border-stone-600"}`}>
                <div className="flex justify-between items-start gap-4">
                  <div>
                    <div className="text-white font-bold">{t.name}</div>
                    <div className="text-stone-400 text-xs mt-1">{t.blurb}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-amber-400 font-extrabold text-lg">£{t.price_gbp.toLocaleString("en-GB")}</div>
                    <div className="text-stone-500 text-[11px]">{t.per_angler ? "per angler" : "whole boat"}</div>
                  </div>
                </div>
              </button>
            ))}

            <h2 className="text-xs font-bold uppercase tracking-widest text-stone-400 pt-4">3 · Your Details</h2>
            <div className="space-y-4">
              <input data-testid="booking-name-input" type="text" placeholder="Lead angler full name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-3 bg-stone-950 border border-stone-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-400 placeholder:text-stone-600" />
              <input data-testid="booking-phone-input" type="tel" placeholder="Mobile phone number" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full px-4 py-3 bg-stone-950 border border-stone-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-400 placeholder:text-stone-600" />
              <input data-testid="booking-email-input" type="email" placeholder="Email for confirmation" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-4 py-3 bg-stone-950 border border-stone-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-400 placeholder:text-stone-600" />
            </div>
          </div>

          {/* Calendar */}
          <div className="lg:col-span-7">
            <h2 className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-4 flex items-center gap-2"><CalendarDays className="w-4 h-4 text-amber-400" /> 2 · Pick A Date</h2>
            <div className="bg-stone-950 border border-stone-800 rounded-2xl p-6" data-testid="booking-calendar">
              <div className="flex items-center justify-between mb-4">
                <button type="button" data-testid="calendar-prev-month" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
                  className="p-2 rounded border border-stone-800 hover:border-amber-400 text-stone-300 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
                <div className="text-white font-bold uppercase tracking-wider text-sm">
                  {cursor.toLocaleString("en-GB", { month: "long", year: "numeric" })}
                </div>
                <button type="button" data-testid="calendar-next-month" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
                  className="p-2 rounded border border-stone-800 hover:border-amber-400 text-stone-300 transition-colors"><ChevronRight className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-mono uppercase text-stone-500 mb-2">
                {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => <div key={d}>{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {grid.map((iso, i) => {
                  if (!iso) return <div key={`x${i}`} />;
                  const past = iso < todayIso;
                  const rem = remaining(iso);
                  const full = rem === 0;
                  const sel = selectedDate === iso;
                  return (
                    <button type="button" key={iso} data-testid={`calendar-day-${iso}`} disabled={past || full}
                      onClick={() => { setSelectedDate(iso); setQuantity(1); }}
                      className={`aspect-square rounded-lg text-sm font-semibold flex flex-col items-center justify-center transition-colors
                        ${sel ? "bg-amber-400 text-stone-950" : past || full ? "text-stone-700 cursor-not-allowed" : "bg-stone-900 hover:border-amber-400 border border-stone-800 text-stone-200"}`}>
                      {parseInt(iso.slice(8), 10)}
                      {!past && rem !== null && !full && rem <= 3 && (
                        <span className={`text-[9px] font-mono ${sel ? "text-stone-800" : "text-amber-400"}`}>{rem} left</span>
                      )}
                      {full && <span className="text-[9px] font-mono text-stone-700">full</span>}
                    </button>
                  );
                })}
              </div>
              {selectedDate && (
                <div className="mt-4 text-xs text-amber-300 font-mono" data-testid="selected-date-label">
                  Selected: {new Date(selectedDate + "T12:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                </div>
              )}
            </div>

            {trip.per_angler && selectedDate && (
              <div className="mt-6 bg-stone-950 border border-stone-800 rounded-2xl p-6">
                <div className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-amber-400" /> Anglers</div>
                <div className="flex items-center gap-4">
                  <button type="button" data-testid="qty-minus" onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 rounded-lg border border-stone-700 text-white font-bold hover:border-amber-400 transition-colors">−</button>
                  <span className="text-2xl font-extrabold text-white w-10 text-center" data-testid="qty-value">{quantity}</span>
                  <button type="button" data-testid="qty-plus" onClick={() => setQuantity(Math.min(maxQty, quantity + 1))}
                    className="w-10 h-10 rounded-lg border border-stone-700 text-white font-bold hover:border-amber-400 transition-colors">+</button>
                  <span className="text-stone-400 text-xs">max {maxQty} on this date</span>
                </div>
              </div>
            )}

            <div className="mt-6 bg-stone-950 border border-stone-800 rounded-2xl p-6">
              <div className="flex justify-between items-center mb-4">
                <span className="text-stone-400 text-xs uppercase tracking-wider">Total due today</span>
                <span className="text-3xl font-extrabold text-white" data-testid="booking-total">£{total.toLocaleString("en-GB")}</span>
              </div>
              {error && <div className="mb-4 text-xs text-red-400 font-semibold" data-testid="booking-error">{error}</div>}
              <button type="submit" data-testid="booking-submit-btn" disabled={submitting}
                className="w-full py-4 bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-stone-950 font-black text-sm uppercase tracking-wider rounded-lg transition-colors flex items-center justify-center gap-2">
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {submitting ? "Opening Secure Checkout…" : "Confirm Date & Pay — Card / Apple Pay"}
              </button>
              <div className="text-center text-[11px] text-stone-500 mt-3">Secure Stripe checkout · Weather cancellations carry full reschedule or refund · Or call 07929834128</div>
            </div>
          </div>
        </form>

        <div className="mt-10 text-xs text-stone-500">
          <Link to="/" className="text-amber-400 hover:text-amber-300" data-testid="back-home-link">← Back to Yellowfin Charters</Link>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
